import { type UseQueryResult } from 'react-query';
import { excludeDecimalFields, skipDecimalConversion } from '../../utils/apiDecimalConfig';

/**
 * Higher-Order Hook that excludes specific fields from decimal conversion
 * All other decimal values will be converted to 4 decimal places
 * 
 * @param fields - Array of field paths to SKIP (preserve raw decimal values)
 * 
 * @example
 * const useGetDataBase = (handleRequest, params) => {
 *   return useQuery(['getData'], async () => {
 *     return await handleRequest(DataService.getData(params));
 *   });
 * };
 * 
 * export default withExcludeDecimalFields([
 *   'exchangeRate',
 *   'conversionRatio',
 *   'items[].ratio'
 * ])(useGetDataBase);
 */
export function withExcludeDecimalFields<TData = any, TError = unknown>(fields: string[]) {
  return function <TParams extends any[]>(
    useHook: (
      handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
      ...params: TParams
    ) => UseQueryResult<TData, TError>
  ) {
    return (
      handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
      ...params: TParams
    ): UseQueryResult<TData, TError> => {
      // Wrap handleRequest to add exclusion config
      const decoratedHandleRequest = <T>(request: Promise<T>) => {
        return handleRequest(excludeDecimalFields(request, fields));
      };

      return useHook(decoratedHandleRequest, ...params);
    };
  };
}

/**
 * Higher-Order Hook to skip ALL decimal conversion for this endpoint
 * All decimal values will be preserved as raw values
 * 
 * @example
 * const useGetPersonalDetailsBase = (handleRequest) => {
 *   return useQuery(['getPersonalDetails'], async () => {
 *     return await handleRequest(ParticipantService.getPersonalDetails());
 *   });
 * };
 * 
 * export default withoutDecimalConversion()(useGetPersonalDetailsBase);
 */
export function withoutDecimalConversion<TData = any, TError = unknown>() {
  return function <TParams extends any[]>(
    useHook: (
      handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
      ...params: TParams
    ) => UseQueryResult<TData, TError>
  ) {
    return (
      handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
      ...params: TParams
    ): UseQueryResult<TData, TError> => {
      // Wrap handleRequest to skip all conversion
      const decoratedHandleRequest = <T>(request: Promise<T>) => {
        return handleRequest(skipDecimalConversion(request));
      };

      return useHook(decoratedHandleRequest, ...params);
    };
  };
}

/**
 * Alias for better readability
 * Same as withExcludeDecimalFields
 */
export const withRawDecimalFields = withExcludeDecimalFields;

